import { type ReviewDto, isReviewInFlight } from "@reviewer73/shared";
import { Strip } from "./strip.js";
import { StatusBadge } from "./status-badge.js";
import { formatCost, formatDuration, repoName } from "../lib/format.js";
import { useElapsedSeconds } from "../lib/use-elapsed-seconds.js";

const SKIP_REASON: Record<string, string> = {
  diff_too_large: "Diff zu gross",
  disabled: "Projekt deaktiviert",
  no_rules: "keine Regeln",
  mr_closed: "MR geschlossen",
  superseded: "durch neuen Push ersetzt",
  cancelled_by_user: "manuell abgebrochen",
};

/**
 * The one review listing row: primary line (project + !MR, time right),
 * quiet meta line below. Shared by Übersicht, Reviews, and Projektdetail so
 * a review reads the same everywhere it appears.
 */
export function ReviewRow({
  review,
  time,
  showProject = true,
}: {
  review: ReviewDto;
  /** Right-aligned time label — the caller decides date vs. time-of-day. */
  time: string;
  showProject?: boolean;
}) {
  // While the run is live there is no durationS yet — tick up from createdAt instead.
  const isLive = isReviewInFlight(review.state);
  const elapsedS = useElapsedSeconds(review.createdAt, isLive);
  const durationS = elapsedS ?? review.durationS;
  return (
    <Strip state={review.state} to={`/reviews/${review.id}`}>
      <div className="px-4 py-3">
        <div className="flex items-baseline justify-between gap-3">
          <span className="min-w-0 truncate font-semibold">
            {showProject ? <>{repoName(review.projectPath)} </> : null}
            <span className="font-mono text-sm font-normal">!{review.mrIid}</span>
          </span>
          <span className="shrink-0 text-sm text-muted-foreground">{time}</span>
        </div>
        {/* Reading order by usefulness: state, then the finding count (the
            one number the reader came for — inked when it is non-zero),
            then timing/cost; the long model name sits last, right-aligned
            and quietest, where it can be ignored. */}
        <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
          <StatusBadge state={review.state} />
          {review.skipReason ? (
            <span>{SKIP_REASON[review.skipReason] ?? review.skipReason}</span>
          ) : null}
          <span aria-hidden>·</span>
          <span
            className={
              review.findingCount > 0 ? "font-semibold text-foreground tabular-nums" : "tabular-nums"
            }
          >
            {review.findingCount} Finding{review.findingCount === 1 ? "" : "s"}
          </span>
          {durationS !== null ? (
            <>
              <span aria-hidden className="hidden sm:inline">·</span>
              <span className="hidden tabular-nums sm:inline">{formatDuration(durationS)}</span>
            </>
          ) : null}
          {review.costUsd !== null ? (
            <>
              <span aria-hidden className="hidden sm:inline">·</span>
              <span className="hidden tabular-nums sm:inline">{formatCost(review.costUsd)}</span>
            </>
          ) : null}
          <span className="ml-auto hidden font-mono text-xs text-muted-foreground/80 md:inline">
            {review.model}
          </span>
        </div>
      </div>
    </Strip>
  );
}

/** Loading placeholder matching the row's real height. */
export function ReviewRowSkeleton({ count = 4 }: { count?: number }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="tile h-[66px] animate-pulse bg-muted/60" />
      ))}
    </div>
  );
}
